import React, {Component} from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import Util from '../../../util/util';
import Person from '../../person/person';
import CreateCommentMutation from '../../../../../mutations/create_comment_mutation';
import {injectIntl} from 'react-intl';
import AddButton from '../../add_button';
import {profilePicSrc} from '../../../../../directApi';
import {isNovaInvocationEnabled, isNovaThreadReadable} from '../../nova-invocation/novaInvocationGate';
import {RichTextEditorDraftJsWrapper} from '../../rich-text-editor-draftjs-wrapper/RichTextEditorDraftJsWrapper';
import {convertEditedHtmlOrNotify} from '../../rich-text-editor-draftjs-wrapper/convertEditedHtml';
import {withinCharacterLimitOrNotify} from '../../rich-text-editor-draftjs-wrapper/characterLimit';
import {NovaReplyTextarea} from './nova-thread/NovaReplyTextarea';
import {isEditorEmpty} from '../../rich-text-editor-draftjs-wrapper/isEditorEmpty';
import {EDITOR_SURFACE, trackEditorSaveFailed, trackEditorSaveSucceeded} from '../../../../../tracking/amplitude/editorEvents';
import {TASK_MODAL_PLUGIN_TOGGLES} from '../taskModalPluginToggles';
import {Button, Detail, EMPHASIS, Icon} from '@forecast-it/design-system';
import {COMPOSER_MODE, NovaComposerModeSwitch} from '../../nova-invocation/NovaComposerModeSwitch';
import {
	getCreditsRefusal,
	getRunState,
	invoke,
	isAtRunCap,
	RUN_CAP,
	subscribeToAnyRunStateChange,
} from '../../nova-invocation/novaInvocationStore';
import {withNovaCreditsBlock} from '../../nova-invocation/novaCredits';
import {formatCreditsReason} from '../../nova-invocation/novaCreditsCopy';
import {toastIfRefused} from '../../nova-invocation/novaRefusalToast';
import {NOVA_RUN_SURFACE} from '../../nova-invocation/novaRunSurface';
import {createToast} from '../../toasts/another-toast/toaster';
import {subscribe, unsubscribe, EVENT_ID} from '../../../../../containers/event_manager';

// The reasons novaInvocationStore.invoke() can hand back instead of running - a reply that hits
// one of these never reaches the model, so it must not read as silently accepted. Mirrors the
// tone of the reasons already shown elsewhere on this surface (see novaInvocationStore's own
// FailureCause copy) rather than inventing a second voice for the same kind of message.
const REPLY_REFUSAL_MESSAGE_ID = {
	already_running: 'nova_thread.reply.refused_already_running',
	at_run_cap: 'nova_thread.reply.refused_at_run_cap',
	no_agent: 'nova_thread.reply.refused_no_agent',
	superseded: 'nova_thread.reply.refused_superseded',
	// Told apart from superseded on purpose: the person pressed Stop themselves, and blaming
	// "something else" for what they just did reads as the product not knowing what happened.
	stopped: 'nova_thread.reply.refused_stopped',
};

// This task's own run does not count against the ceiling: replying to a thread that is already
// working is refused for a different reason, by the store, and reading the ceiling as the blocker
// there would state the wrong one.
const capBlocksReplies = taskId => isAtRunCap() && !getRunState(taskId).isRunning;
const sessionTurnIdFor = taskId => getRunState(taskId).lastTurn?.id ?? null;

class CommentFooterSection extends Component {
	constructor(props) {
		super(props);
		this.state = {
			// True between a reply leaving this box and its run resolving, so the send control cannot
			// be pressed twice for one message.
			isSendingReply: false,
			// A task with no Nova thread never shows the mode switch at all (see hasNovaThread()),
			// so this default is never seen by that task - only one that already has a thread the
			// switch could put it into reply mode for.
			mode: COMPOSER_MODE.COMMENT,
			// Whether the three-run ceiling is what stops a reply right now. Seeded here rather
			// than left false so a composer mounted at the ceiling is already correct on its first
			// paint, before the subscription below has had anything to report.
			capBlocks: capBlocksReplies(props.task.id),
			// The run this session holds for the task, by id. hasNovaThread() reads it, so a first run
			// finishing with the modal open has to re-render this composer - and only that does.
			sessionTurnId: sessionTurnIdFor(props.task.id),
		};
		this.syncRunState = this.syncRunState.bind(this);

		this.inputContainer = React.createRef();

		this.viewerProfileSrc = profilePicSrc(props.viewer.profilePictureId);
		this.plateJsMentionsArray =
			props.project.projectPersons &&
			props.project.projectPersons.edges
				.filter(projectPerson => {
					return projectPerson.node.person.active;
				})
				.map(projectPerson => {
					return {
						id: projectPerson.node.person.id,
						text: projectPerson.node.person.firstName + ' ' + projectPerson.node.person.lastName,
					};
				});
	}

	componentDidMount() {
		// Landing directly in reply mode is a one-time thing about how this task modal opened, not a
		// state this keeps re-deriving - startInReplyMode only ever gets read here, on the mount that
		// is the modal's own landing, so a later prop change (an incoming socket update, say) never
		// switches the composer back into reply mode or steals focus from someone already typing.
		//
		// It waits for the thread, though. A modal built from cached data taken before the run
		// finished has no thread yet, and the landing applies when the fresh data brings it in.
		this.replyLandingPending = !!this.props.startInReplyMode;
		this.applyReplyLanding();
		subscribe(EVENT_ID.NOVA_REQUEST_REPLY, this.handleReplyRequested);
		// A run finishing anywhere frees the slot this composer is waiting for, so the chip has to
		// hear about runs on other tasks too - hence the whole-set subscription rather than this
		// task's own. Read once on subscribe as well: a run can start or end between this
		// component's first render and this line, and that write dispatches to nobody yet.
		this.syncRunState();
		this.unsubscribeFromRuns = subscribeToAnyRunStateChange(this.syncRunState);
	}

	componentDidUpdate(prevProps) {
		this.applyReplyLanding();
		// Reply mode cannot send while the allowance is spent, the three-run ceiling is reached, or
		// the newest turn is an error still to be retried or dismissed, so the composer goes back to
		// Comment and keeps the half-written reply for when Reply is available again. Leaving it in
		// reply mode would hand the person a live Send that would be refused after they pressed it.
		if (!this.canReplyToNova() && this.state.mode === COMPOSER_MODE.REPLY_TO_NOVA) {
			this.setState({mode: COMPOSER_MODE.COMMENT});
		}
		if (prevProps.viewer.profilePictureId !== this.props.viewer.profilePictureId) {
			this.viewerProfileSrc = profilePicSrc(this.props.viewer.profilePictureId);
		}
		if (prevProps.project !== this.props.project) {
			this.plateJsMentionsArray =
				this.props.project.projectPersons &&
				this.props.project.projectPersons.edges
					.filter(projectPerson => {
						return projectPerson.node.person.active;
					})
					.map(projectPerson => {
						return {
							id: projectPerson.node.person.id,
							text: projectPerson.node.person.firstName + ' ' + projectPerson.node.person.lastName,
						};
					});
		}
	}

	componentWillUnmount() {
		unsubscribe(EVENT_ID.NOVA_REQUEST_REPLY, this.handleReplyRequested);
		if (this.unsubscribeFromRuns) this.unsubscribeFromRuns();
	}

	// Every Nova state change on the page reaches this - a run on another row, a read being marked -
	// so it only sets state when one of the two things this composer shows has actually moved.
	syncRunState() {
		const capBlocks = capBlocksReplies(this.props.task.id);
		const sessionTurnId = sessionTurnIdFor(this.props.task.id);
		if (capBlocks !== this.state.capBlocks || sessionTurnId !== this.state.sessionTurnId) {
			this.setState({capBlocks, sessionTurnId});
		}
	}

	// The conversation's own Reply to Nova control, which sits in the newest turn's action row and so
	// has no way to reach this composer directly - the two are siblings under the task modal, not
	// parent and child. Taken off the shared bus for the same reason the agent picker's toggle is.
	//
	// The bus is global and a composer can be mounted for more than one task, so each one answers
	// only for its own task. The guards are the mode switch's own: a person who may not reply is not
	// switched into a mode whose send would be refused.
	handleReplyRequested = taskId => {
		if (taskId !== this.props.task.id) return;
		if (!this.hasNovaThread() || !this.canReplyToNova()) return;
		// Same two-step as the landing case above: reply mode's field is not in the DOM until the
		// mode change has rendered, so focus waits for the callback.
		this.setState({mode: COMPOSER_MODE.REPLY_TO_NOVA}, () => this.focusComposerInput());
	};

	// Whether this task has a Nova conversation to reply to at all - the one thing that decides
	// whether the mode switch renders (see render()) and whether a reply has anywhere to go (see
	// submitNovaReply()). Read from novaThread rather than inferred from the mode state, since the
	// mode itself only ever gets to reply.* through the switch this same fact gates.
	//
	// Gated on the feature flag as well, the same check NovaConversationThread makes: an account with
	// the flag off but a thread already saved - one run before it was switched off, or a thread from
	// another surface - was still offered the mode switch, and so a way to start a run the rest of
	// the product had stopped offering.
	hasNovaThread() {
		if (!isNovaThreadReadable()) return false;
		// Deliberately not gated on whether this person may act. Somebody who cannot start a run can
		// still read the conversation, and the switch is how they see that Nova is on offer here at
		// all — it renders disabled with its reason instead of disappearing. canReplyToNova is what
		// refuses the mode change.
		if (Array.isArray(this.props.novaThread) && this.props.novaThread.length > 0) return true;
		// The served list was fetched when the modal opened and is never refetched, so a first run
		// started with the modal open lands here only in the store. Reading the list alone left the
		// composer with no Reply to Nova under an answer that was on screen, and made the answer's own
		// Reply to Nova do nothing. A discard forgets the store's turn, so this goes with it.
		return !!(this.props.task && getRunState(this.props.task.id).lastTurn);
	}

	// The run a reply continues from, which decides both the thread it joins and the agent that
	// answers it. The most recent run rather than the first - changing the agent makes a later run
	// the current one, and a reply must go to that agent rather than the one the conversation opened
	// with.
	//
	// The server list this component is handed was fetched when the modal opened and is never
	// refetched, so it does not know about a run started since - and changing the agent starts one,
	// on a new thread. Reading the list alone sent every reply after an agent change back to the
	// retired thread under the previous agent, which made that thread the task's newest again and
	// put the old agent's conversation back on screen.
	//
	// A turn in the store for this task that the server list has no record of is by construction
	// newer than the list: the list is a snapshot, and the store only holds what this session ran.
	mostRecentNovaRun() {
		const {novaThread} = this.props;
		// The served list itself, not hasNovaThread(): that also counts a run this session started, and
		// the list can be empty or absent then.
		const fromServer =
			isNovaThreadReadable() && Array.isArray(novaThread) && novaThread.length > 0
				? novaThread[novaThread.length - 1]
				: null;
		// Whichever ran later wins, rather than the session turn winning by default. The served list is
		// not always the older of the two: open the modal after another tab has changed the agent and
		// the list already holds that newer run, while this session still remembers one from before it.
		// Preferring the session turn there would reply on a thread the thread view no longer shows.
		const ranLater = (a, b) => !a?.ranAt || !b?.ranAt || a.ranAt >= b.ranAt;
		// lastTurn, not outcome.turn: the outcome is replaced by every run and several carry no turn at
		// all - a refusal, a failure, a stop. Reading the outcome alone meant that after a reply was
		// refused, the next reply fell back to the snapshot and rejoined the retired thread under the
		// previous agent, which is the case this is here to prevent.
		const fromThisSession = getRunState(this.props.task.id).lastTurn;
		if (fromThisSession && (!fromServer || fromThisSession.id !== fromServer.id)) {
			// Unknown timestamps fall back to trusting this session, which is the case that matters in
			// practice: a run this tab just performed that the list predates.
			return ranLater(fromThisSession, fromServer) ? fromThisSession : fromServer;
		}
		return fromServer;
	}

	// Whether replying is on offer here at all. A reply starts a run, so it needs the same
	// permission acting on the task needs, and the same AI access; commenting is unaffected,
	// because a task comment is not an act on the agent. Absent means allowed, so a caller that
	// does not pass it keeps its behaviour.
	//
	// Both of these are standing: this person cannot grant themselves the permission or turn AI
	// access on from a composer. So when either is missing the mode switch is not rendered at all
	// and the box is the comment box it always was - rather than a pair of tabs with one of them
	// permanently greyed, advertising a mode they will never reach.
	replyModeOffered() {
		return this.props.canReplyToNova !== false && isNovaInvocationEnabled();
	}

	// Whether a reply could actually be sent right now. Everything replyModeOffered() asks, plus
	// the blocks that clear on their own - and those are the ones the chip stays visible for,
	// wearing its reason. The newest turn being an error is one of them: it has to be retried or
	// dismissed before the conversation can go on.
	canReplyToNova() {
		return this.replyModeOffered() && !this.newestTurnFailed() && !this.creditsBlocked() && !this.atRunCap();
	}

	newestTurnFailed() {
		return !!this.props.novaNewestTurnFailed;
	}

	creditsBlocked() {
		return !!(this.props.novaCreditsBlock && this.props.novaCreditsBlock.blocked);
	}

	// A reply starts a run like any other, so the three-run ceiling applies to it. This task's own
	// run does not count against it: replying to a thread that is already working is refused for a
	// different reason, by the store, and reading the ceiling as blocking here would state the
	// wrong one. Read live rather than held in state - see the subscription in componentDidMount.
	atRunCap() {
		return this.state.capBlocks;
	}

	// Only the blocks that clear on their own ever reach a control: the standing reasons take the
	// switch away instead of labelling it. Credits first, then the ceiling - a run that cannot be
	// paid for is the truer reason than one merely waiting for a slot - then the failed turn, which
	// Try again could not get past either while one of the other two holds.
	cannotReplyReason() {
		if (this.creditsBlocked()) {
			return formatCreditsReason(this.props.intl, this.props.novaCreditsBlock && this.props.novaCreditsBlock.resetsOn);
		}
		if (this.newestTurnFailed() && !this.atRunCap()) {
			return this.props.intl.formatMessage({id: 'nova_thread.reply.reason_last_run_failed'});
		}
		return this.props.intl.formatMessage({id: 'nova_invocation.control.reason_run_cap'}, {count: RUN_CAP});
	}

	/**
	 * Whether to explain, above the box, why replying is off right now.
	 *
	 * Only where replying is on offer at all and a block that clears is what stops it: a standing
	 * permission takes the switch away entirely, and there is nothing left for a line to be about.
	 * Only with a conversation above, too - a line about replying to a thread that does not exist
	 * explains nothing.
	 *
	 * Withheld while the conversation is already carrying the notice for the same block: the
	 * out-of-credits notice, or the error a failed turn ended in. Each says the same thing at more
	 * length a few centimetres higher up, and two statements of one fact read as two different
	 * facts.
	 */
	showsBlockedReplyLine() {
		if (!this.replyModeOffered() || this.canReplyToNova() || !this.hasNovaThread()) return false;
		if (this.creditsBlocked()) return !getCreditsRefusal(this.props.task.id);
		return this.atRunCap();
	}

	setComposerMode(mode) {
		// The composer never enters reply mode for somebody who cannot reply. The switch refuses the
		// change itself, and so does this: a mode set from anywhere else — the landing on mount, a
		// later caller — must not get round it.
		if (mode === COMPOSER_MODE.REPLY_TO_NOVA && !this.canReplyToNova()) return;
		this.setState({mode});
	}

	// Settles the reply landing the first time there is a thread to reply to, and only then: once a
	// thread is there, the landing is used up whether or not this person may reply.
	applyReplyLanding() {
		if (!this.replyLandingPending || !this.hasNovaThread()) return;
		this.replyLandingPending = false;
		if (!this.canReplyToNova()) return;
		// Focused in the setState callback, not straight after it: the two modes render different
		// fields (reply mode a plain textarea, comment mode a rich editor), so the field to put the
		// cursor in does not exist in the DOM until this mode change has rendered.
		this.setState({mode: COMPOSER_MODE.REPLY_TO_NOVA}, () => this.focusComposerInput());
	}

	// Puts the cursor in whichever field the current mode renders.
	//
	// Reached for generically, by DOM query, rather than through a ref: the three fields this has to
	// cover are a plain textarea (reply mode) and two rich editors, and PlateJS's wrapper
	// (RichTextEditorDraftJsWrapper) exposes no imperative focus at all on its ref contract
	// (RichTextEditorFunctionsType only has getHtmlValue/reset), so there is no single handle to call
	// through. Both editors render their editable surface as a contentEditable region underneath,
	// which is the other half of what this looks for.
	focusComposerInput() {
		// Reply mode's own field by name, because both fields are mounted at once now and the hidden
		// one is still findable - a generic query would put the cursor in whichever happened to come
		// first in the DOM rather than in the one on screen.
		if (this.state.mode === COMPOSER_MODE.REPLY_TO_NOVA && this.novaReplyField) {
			this.novaReplyField.focus();
			return;
		}
		const container = this.inputContainer.current;
		const editable = container && container.querySelector('[contenteditable="true"], input');
		if (editable) editable.focus();
	}

	submitComment() {
		if (this.state.mode === COMPOSER_MODE.REPLY_TO_NOVA && this.hasNovaThread()) {
			this.submitNovaReply();
			return;
		}
		this.submitCreateComment();
	}

	// Enter's entry point. Goes straight to submitCreateComment rather than submitComment:
	// that dispatcher routes to submitNovaReply in reply mode, which starts a paid model
	// run, and the Plate editor never means "reply" - it sits in the display:none half of
	// the composer whenever reply mode is active.
	handleSubmitShortcut() {
		// Read ONCE and pass it on. getHtmlValue is not a read: it runs removeInlinePopups,
		// which removeNodes()s every mention-input node before serialising. Calling it as a
		// predicate and again for the payload would serialise twice per submit, and would
		// mutate the document even on the decline path.
		const html = this.plateRichTextField?.getHtmlValue();

		// false hands the key back so the editor still inserts a paragraph. Returning nothing
		// would swallow the keystroke: no comment posted, and no line break either.
		if (isEditorEmpty(html)) return false;
		this.submitCreateComment(html);
	}

	/** `html` is supplied by the Enter path, which has already serialised. The add button omits it. */
	submitCreateComment(html) {
		// Converted before the editor is reset, and the reset skipped entirely if the
		// conversion fails: the user's text is only in the editor at this point, so
		// clearing it first would destroy the comment we just failed to save.
		const commentText = convertEditedHtmlOrNotify(
			html ?? this.plateRichTextField.getHtmlValue(),
			this.props.intl,
			EDITOR_SURFACE.newComment
		);
		if (commentText === null) return;
		// Checked before the reset for the same reason: the editor holds the only copy of the text.
		const characterLimit = this.props.viewer?.company?.characterLimit;
		if (!withinCharacterLimitOrNotify(commentText, characterLimit, this.props.intl, EDITOR_SURFACE.newComment)) return;
		this.plateRichTextField.reset();

		const onSuccess = () => {
			this.props.toggleComments(true);
			this.props.scrollToBottom();
		};

		Util.CommitMutation(
			CreateCommentMutation,
			{
				comment: commentText,
				taskId: this.props.task.id,
				person: this.props.viewer,
				currentDate: Moment(),
			},
			() => {
				trackEditorSaveSucceeded(EDITOR_SURFACE.newComment);
				onSuccess();
			},
			true,
			null,
			// The fallback runs either way, so the correlation-ID error modal and the retry
			// are untouched. A scheduled retry is not a failure yet, which is what its
			// return value says.
			(transaction, onFailure) => {
				if (!onFailure(transaction)) {
					trackEditorSaveFailed(EDITOR_SURFACE.newComment, 'mutation');
				}
			}
		);
	}

	// A reply never touches CreateCommentMutation - it starts a Nova run carrying this thread's id
	// and stays in the conversation, whatever the outcome. Nothing here posts to the task's
	// comments, and nothing above ever calls this for comment mode.
	submitNovaReply() {
		const mostRecentRun = this.mostRecentNovaRun();
		const threadId = mostRecentRun ? mostRecentRun.threadId : null;
		const agentId = mostRecentRun ? mostRecentRun.agentId : null;
		// The mode switch only renders once a thread exists (hasNovaThread), so this should always
		// resolve - but a reply with nowhere to go must stop here rather than fall back to posting
		// itself as a comment instead.
		if (!threadId || !agentId) return;

		// Read straight off the textarea. A reply is sent as plain text - the model receives the
		// characters typed and nothing else - so this mode uses a plain field rather than either rich
		// editor, and there is no editor state to interrogate or reset.
		const messageText = this.novaReplyField ? this.novaReplyField.value : '';
		if (!messageText.trim()) return;

		// Cleared straight away so the box reads as sent, but put back if the send did not happen. The
		// text is the only copy of what the person wrote; losing it to a refusal they did not cause -
		// a run already in flight, the run ceiling, a network fault - costs them the message itself.
		this.novaReplyField.value = '';
		this.setState({isSendingReply: true});
		this.props.scrollToBottom();

		const restore = () => {
			this.setState({isSendingReply: false});
			// Only if the person has not started writing something else in the meantime, which would
			// otherwise be overwritten by the message they already watched leave the box.
			if (this.novaReplyField && !this.novaReplyField.value) {
				this.novaReplyField.value = messageText;
			}
		};

		const {viewer} = this.props;
		invoke(this.props.task.id, agentId, {
			message: messageText,
			threadId,
			surface: NOVA_RUN_SURFACE.TASK_MODAL,
			// Who to name on the turn a refusal stands in for. A refusal saves the reply but hands back
			// no run, so nothing in that turn can say who wrote it unless the sender does - and the
			// sender is whoever is looking at this composer.
			replyAuthor: {
				fullName: [viewer.firstName, viewer.lastName].filter(Boolean).join(' ') || null,
				profilePictureId: viewer.profilePictureId ?? null,
			},
		})
			.then(result => {
				// A run that started is already visible in the thread itself (see
				// NovaConversationThread), which watches the same store - so only the ways a reply can
				// fail to become a turn are handled here.
				//
				// Refused for a spent allowance: the thread now shows this message with the notice
				// under it, so the box stays empty and there is nothing else to say.
				if (
					(result.kind === 'refused' && result.reason === 'out_of_credits') ||
					(result.kind === 'failed' && result.cause === 'out_of_credits' && !result.turn)
				) {
					this.setState({isSendingReply: false});
					return;
				}
				if (result.kind === 'refused') {
					restore();
					// Stop already said what happened, wherever it was pressed; the reply is back in the box.
					if (result.reason !== 'stopped') this.notifyReplyRefused(result.reason);
					return;
				}
				if (result.kind === 'failed' || result.kind === 'cancelled') {
					// A saved record is not enough on its own: a turn renders the person's words from the
					// user-role text the run service reports it actually sent, and a run that failed
					// before the model saw anything saves none. The turn is then an answer-less failure
					// with nothing above it, so the box is the only place the message still exists.
					//
					// Where that text is there, the message and the failure Nova reported are both on
					// screen already. Putting it back would hand the person a second copy of something
					// they can see was sent, and sending it again is the obvious next move; the box
					// stays empty, and the thread is what says the run failed.
					// Two ways the thread can already hold it: a run that saved a turn carrying the text,
					// or a refusal that wrote the text down before refusing - which leaves as an error
					// and so hands back no turn at all, only the fact that it recorded one.
					if ((result.turn && result.turn.userMessageText) || result.replyRecorded) {
						this.setState({isSendingReply: false});
						return;
					}
					// Nothing was saved, so the message only ever existed in this box. It goes back with
					// a word about why - except after a Stop, which already said so, and after an expired
					// session or a lost connection, which the platform's own error flow has explained.
					restore();
					if (result.kind === 'cancelled' || result.reported) return;
					if (result.cause === 'generic' || !toastIfRefused(result, this.props.intl.formatMessage)) {
						this.notifyReplyFailed();
					}
					return;
				}
				this.setState({isSendingReply: false});
			})
			.catch(() => {
				restore();
				this.notifyReplyFailed();
			});
	}

	// Enter sends the reply, Shift+Enter starts a new line. Sending on Enter is what the rest of this
	// composer's shape promises - it is a message box with a send arrow in its corner, not a document
	// - and the arrow keeps working for anyone who reaches for it instead.
	handleNovaReplyKeyDown(e) {
		if (e.key !== 'Enter' || e.shiftKey) return;
		// Same guard as the send control: Enter is the other way to send, and the box is still on
		// screen while the run resolves.
		if (this.state.isSendingReply) {
			e.preventDefault();
			return;
		}
		// Enter while an IME candidate window is open is the composition being accepted, not the
		// message being sent; sending here would cut a word in half for anyone typing a language
		// that needs one.
		if (e.nativeEvent && (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229)) return;
		e.preventDefault();
		this.submitComment();
	}

	notifyReplyFailed() {
		createToast({duration: 5000, message: this.props.intl.formatMessage({id: 'nova_thread.reply.failed'})});
	}

	notifyReplyRefused(reason) {
		const messageId = REPLY_REFUSAL_MESSAGE_ID[reason] || 'nova_invocation.failure.cause.generic';
		createToast({duration: 5000, message: this.props.intl.formatMessage({id: messageId})});
	}

	render() {
		if (this.props.isTaskReadOnly) return null;

		const {formatMessage} = this.props.intl;
		const {viewer, isTaskReadOnly} = this.props;
		const {isSendingReply, mode} = this.state;
		const hasNovaThread = this.hasNovaThread();
		const isReplyMode = hasNovaThread && mode === COMPOSER_MODE.REPLY_TO_NOVA;
		const sendLabelId = `nova-reply-send-label-${this.props.task.id}`;

		return (
			<div
				ref={this.inputContainer}
				className={
					'task-modal-v3-comments-footer-section' +
					(this.props.showFooterSection ? '' : ' comments-footer-section-hidden')
				}
				tabIndex="-1"
				data-cy="task-modal-comments-section"
				data-userpilot={'task-modal-comments-section'}
			>
				{hasNovaThread && !isTaskReadOnly && this.replyModeOffered() && (
					// The one mode affordance this composer gets: a switch between posting a comment and
					// replying in the Nova conversation above it, not a second input or a second send
					// control (see submitComment's own routing). It sits above the composer rather than
					// inside it, so it reads as a question about the whole box it precedes.
					<div className="nova-reply-mode-switch" data-cy="task-modal-comment-mode-switch">
						<NovaComposerModeSwitch
							value={mode}
							onValueChange={value => this.setComposerMode(value)}
							label={formatMessage({id: 'nova_thread.reply.mode_label'})}
							commentLabel={formatMessage({id: 'nova_thread.reply.mode_comment'})}
							replyLabel={formatMessage({id: 'nova_thread.reply.mode_reply'})}
							replyDisabled={!this.canReplyToNova()}
							replyDisabledReason={this.cannotReplyReason()}
						/>
					</div>
				)}
				{this.showsBlockedReplyLine() && (
					// Auxiliary, and sitting between the switch and the box rather than inside either:
					// it is about what the box will do with what is typed next, which belongs to both.
					<div className="nova-reply-blocked-line" data-cy="task-modal-nova-reply-blocked">
						<Icon icon="sparkle" size="s" color="auxiliary" aria-hidden="true" />
						<Detail color="auxiliary">
							{`${this.cannotReplyReason()} ${formatMessage({id: 'nova_thread.reply.blocked_still_comment'})}`}
						</Detail>
					</div>
				)}
				<div className="comment-lower">
					{!isTaskReadOnly ? (
						<>
							<div className="person-profile-picture">
								<Person
									name={`${viewer.firstName} ${viewer.lastName}`}
									showName={false}
									showRole={false}
									imageSize="medium"
									// The whole hexagon, points and all - see Person's `unclipped`.
									unclipped
									imageSrc={this.viewerProfileSrc}
								/>
							</div>
							<div className={'new-comment-box' + (isReplyMode ? ' new-comment-box-nova-reply' : '')}>
								{/* The editor sits in a wrapper of its own so reply mode can give the field
								    itself the tinted, rounded outline the conversation's own cards have -
								    rather than tinting the whole footer row, which would say the surrounding
								    chrome belongs to the conversation too. The wrapper is inert in comment
								    mode. */}
								{/* Both fields stay mounted and the inactive one is hidden, rather than each
								    unmounting the other. Switching modes with something half-written used to
								    destroy it - a comment on the way to reply mode, a reply on the way back -
								    and a draft is not the composer's to throw away because the person looked
								    at the other tab. */}
								<div className="nova-reply-composer-field" style={isReplyMode ? undefined : {display: 'none'}}>
									{/* A plain textarea, not either rich editor: a reply reaches the model as the
									    characters typed and nothing else, so offering formatting, mentions or
									    pasted markup would advertise something that does not survive the send.
									    Styled to match the comment composer beside it exactly - the two are the
									    same control in two modes, and only the placeholder and the send
									    affordance differ. */}
									<NovaReplyTextarea
										ref={e => (this.novaReplyField = e)}
										data-cy="task-modal-nova-reply-input"
										// The composer's own mode chip, not a second key saying the same two words:
										// the placeholder and the tab that switches to it name one thing, and one
										// key is what keeps them naming it identically in every locale.
										placeholder={formatMessage({id: 'nova_thread.reply.mode_reply'})}
										onKeyDown={this.handleNovaReplyKeyDown.bind(this)}
									/>
								</div>
								<div style={isReplyMode ? {display: 'none'} : undefined}>
									<RichTextEditorDraftJsWrapper
										id={'task-modal-comment-input'}
										data-cy={'task-modal-comment-input'}
										peopleMentions={this.plateJsMentionsArray}
										editorRef={e => (this.plateRichTextField = e)}
										initialDraftJsContent={''}
										surface={EDITOR_SURFACE.newComment}
										maxHeight={'190px'}
										placeholder={formatMessage({id: 'cards.comment'})}
										pluginToggles={TASK_MODAL_PLUGIN_TOGGLES}
										imageAdapter={this.props.imageAdapter}
										onSubmitShortcut={this.handleSubmitShortcut.bind(this)}
									/>
								</div>
								{isReplyMode ? (
									// Replying to Nova gets a standing send control rather than the comment
									// footer's focus-revealed AddButton: a reply is the only thing in this modal
									// that spends money and starts a run, so the way to send it should not be
									// something a person has to discover by clicking into the box first. Same
									// EMPHASIS.HIGH primary the ticketing reply composer sends with.
									<div className="nova-reply-send" data-cy="task-modal-nova-reply-send">
										{/* The design-system Button derives its own aria-label by stringifying its
										    children and applies it after the caller's props, so an aria-label passed
										    here is discarded and an icon-only button ends up named "arrowRight".
										    aria-labelledby outranks aria-label in the accessible-name algorithm, so
										    pointing it at a visually hidden label is what survives. */}
										<span id={sendLabelId} className="visually-hidden">
											{formatMessage({id: 'nova_thread.reply.send_label'})}
										</span>
										{/* Carries no disabled state. Sending an empty reply costs nothing —
										    submitNovaReply returns before invoking on blank text — so the standing
										    send stays legible as the one way to send rather than becoming something
										    that has to be woken up. */}
										<Button
											emphasis={EMPHASIS.HIGH}
											icon="arrowRight"
											// Disabled only while this box's own reply is in flight, so one
											// message cannot be sent twice by a second press.
											disabled={isSendingReply}
											onPress={this.submitComment.bind(this)}
											aria-labelledby={sendLabelId}
										/>
									</div>
								) : (
									<AddButton onClick={this.submitComment.bind(this)} customClass="add-comment-button" />
								)}
							</div>
						</>
					) : null}
				</div>
			</div>
		);
	}
}

CommentFooterSection.propTypes = {
	viewer: PropTypes.object.isRequired,
	project: PropTypes.object.isRequired,
	isTaskReadOnly: PropTypes.bool.isRequired,
	showFooterSection: PropTypes.bool.isRequired,
	task: PropTypes.object.isRequired,
	// The task's saved Nova conversation, oldest run first - the same data NovaConversationThread
	// renders (and the same prop name), passed down explicitly rather than read off `task` so the
	// composer's dependency on it is visible at the call site. Absent or empty means no thread, and
	// the mode switch this unlocks stays hidden (see hasNovaThread()).
	novaThread: PropTypes.array,
	// True for exactly one mount: the task modal landing on an already-read Nova answer (see
	// novaLanding.ts). Has no effect at all for a task with no Nova thread (hasNovaThread() gates
	// it), which is what keeps a plain task with no conversation landing exactly as it always has.
	startInReplyMode: PropTypes.bool,
};

export default injectIntl(withNovaCreditsBlock(CommentFooterSection));
